/**
 * OnboardingModal - Multi-step onboarding for new organizers
 * PRD: PRD-onboarding-organisateur.md
 */

import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Icon } from '../../../pages/DesignSystem';
import { OrganizerProfile } from '../../../types/organizer.types';
import { OnboardingStep, toReadableErrorMessage } from '../../../hooks/useOnboarding';
import WelcomeStep from './steps/WelcomeStep';
import ProfileStep from './steps/ProfileStep';
import ReadyStep from './steps/ReadyStep';
import './OnboardingModal.css';

interface OnboardingModalProps {
  isOpen: boolean;
  organizerProfile: OrganizerProfile | null;
  userId: string;
  currentStep: OnboardingStep;
  onNextStep: () => void;
  onPrevStep: () => void;
  onComplete: () => Promise<void>;
  onProfileUpdate: (data: Partial<OrganizerProfile>) => Promise<void>;
}

const STEP_CONFIG: Record<OnboardingStep, { index: number; title: string }> = {
  welcome: { index: 0, title: 'Welcome' },
  profile: { index: 1, title: 'Profile Setup' },
  ready: { index: 2, title: 'Ready!' },
};

const TOTAL_STEPS = 3;

/**
 * OnboardingModal - Guides new organizers through profile setup
 */
const OnboardingModal: React.FC<OnboardingModalProps> = ({
  isOpen,
  organizerProfile,
  currentStep,
  onNextStep,
  onPrevStep,
  onComplete,
  onProfileUpdate,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track form data for live preview
  const [previewData, setPreviewData] = useState<Partial<OrganizerProfile>>({});

  // Initialize preview data with current profile
  useEffect(() => {
    if (organizerProfile && isOpen) {
      setPreviewData(organizerProfile);
    }
  }, [organizerProfile, isOpen]);

  // Handle form data changes for live preview
  const handleFormDataChange = useCallback((data: Partial<OrganizerProfile>) => {
    setPreviewData(prev => ({ ...prev, ...data }));
  }, []);

  // Handle profile form submission
  const handleProfileSubmit = useCallback(async (data: Partial<OrganizerProfile>) => {
    setIsSubmitting(true);
    try {
      await onProfileUpdate(data);
      onNextStep();
    } catch (error) {
      console.error('Error updating profile:', error);
      // 🔧 FIX (item 2 - PRD robustesse réseau faible): rendre l'échec visible.
      // Avant ce fix, seul un console.error était émis : le bouton "Continuer"
      // revenait sans explication et l'utilisateur ne comprenait pas pourquoi.
      toast.error(toReadableErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [onProfileUpdate, onNextStep]);

  // Handle final completion.
  // 🔧 FIX (item 1 - PRD robustesse réseau faible): retourne un booléen de succès
  // réel au lieu d'avaler l'erreur en silence. ReadyStep ne doit naviguer vers le
  // dashboard QUE si ce booléen est `true` — sinon `onboarding_completed` peut être
  // resté `false` en base (réseau faible) alors que l'app affichait "c'est prêt".
  const handleComplete = useCallback(async (): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      await onComplete();
      return true;
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error(toReadableErrorMessage(error));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [onComplete]);

  if (!isOpen) return null;

  const stepConfig = STEP_CONFIG[currentStep];
  const canGoBack = stepConfig.index > 0;

  return (
    <div className="onboarding-modal__backdrop">
      <div className="onboarding-modal__container">
        {/* Header */}
        <div className="onboarding-modal__header">
          <div className="onboarding-modal__header-left">
            {canGoBack && (
              <button
                className="onboarding-modal__back"
                onClick={onPrevStep}
                disabled={isSubmitting}
                aria-label="Go back"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>
            )}
            <h2 className="onboarding-modal__title">{stepConfig.title}</h2>
          </div>
          <div className="onboarding-modal__step-indicator">
            Step {stepConfig.index + 1}/{TOTAL_STEPS}
          </div>
        </div>

        {/* Content */}
        <div className="onboarding-modal__content">
          <div className="onboarding-modal__main">
            {currentStep === 'welcome' && (
              <WelcomeStep onContinue={onNextStep} />
            )}
            {currentStep === 'profile' && (
              <ProfileStep
                profile={organizerProfile}
                onSubmit={handleProfileSubmit}
                onFormDataChange={handleFormDataChange}
                isSubmitting={isSubmitting}
              />
            )}
            {currentStep === 'ready' && (
              <ReadyStep
                onComplete={handleComplete}
                isSubmitting={isSubmitting}
              />
            )}
          </div>

          {/* Preview on desktop */}
          {currentStep === 'welcome' ? (
            <div className="onboarding-modal__preview onboarding-modal__preview--welcome">
              <div className="onboarding-modal__preview-logo">
                <img
                  src="/images/logoverttexte.webp"
                  alt="What 2 Do"
                  className="onboarding-modal__logo-img"
                />
              </div>
            </div>
          ) : (
            <div className="onboarding-modal__preview">
              <div className="onboarding-modal__preview-header">
                <Icon name="Eye" size={16} />
                Your Public Profile
              </div>
              <div className="onboarding-modal__preview-content">
                <ProfilePreview profile={previewData} />
              </div>
            </div>
          )}
        </div>

        {/* Step indicators */}
        <div className="onboarding-modal__footer">
          <div className="onboarding-modal__dots">
            {(['welcome', 'profile', 'ready'] as OnboardingStep[]).map((step, i) => (
              <span
                key={step}
                className={`onboarding-modal__dot ${
                  i === stepConfig.index ? 'onboarding-modal__dot--active' : ''
                } ${i < stepConfig.index ? 'onboarding-modal__dot--completed' : ''}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ProfilePreview - Shows how the profile looks to tourists
 */
interface ProfilePreviewProps {
  profile: Partial<OrganizerProfile> | null;
}

const ProfilePreview: React.FC<ProfilePreviewProps> = ({ profile }) => {
  const hasPhoto = profile?.organizerImage;
  const hasName = profile?.organizer && profile.organizer.trim() !== '';
  const hasSubtitle = profile?.subtitle && profile.subtitle.trim() !== '';
  const hasLocation = profile?.location;

  return (
    <div className="profile-preview">
      {/* Avatar */}
      <div className="profile-preview__avatar">
        {hasPhoto ? (
          <img
            src={profile?.organizerImage}
            alt={profile?.organizer || 'Profile'}
            className="profile-preview__avatar-img"
          />
        ) : (
          <div className="profile-preview__avatar-placeholder">
            <Icon name="User" size={32} />
          </div>
        )}
      </div>

      {/* Name */}
      <h3 className="profile-preview__name">
        {hasName ? profile?.organizer : 'Your Name'}
      </h3>

      {/* Bio/Subtitle */}
      {hasSubtitle && (
        <p className="profile-preview__subtitle">
          {profile?.subtitle}
        </p>
      )}

      {/* Location */}
      <div className="profile-preview__location">
        <Icon name="MapPin" size={14} />
        <span>{hasLocation ? profile?.location : 'Your Location'}</span>
      </div>

      {/* Status indicator - only shown when name is missing */}
      {!hasName && (
        <div className="profile-preview__status">
          <span className="profile-preview__status--incomplete">
            <Icon name="AlertCircle" size={14} />
            Add your name to continue
          </span>
        </div>
      )}

      {/* Helper text */}
      <p className="profile-preview__helper">
        This is how tourists will see your profile
      </p>
    </div>
  );
};

export default OnboardingModal;
